import { Loader2 } from "lucide-react"
import { useEffect, useState } from "react"
import { ImageLightboxDialog } from "@/components/image-lightbox-dialog"
import { PlayBadge } from "@/components/media/asset-media-overlay"
import { Button, Textarea } from "@/components/ui/primitives"
import { WorkspaceDialog } from "@/components/workspace/workspace-dialog"
import { videoFirstFrameSrc } from "@/lib/video-timeline"

export type CorrectionPromptPreset = {
  id: string
  label: string
  prompt: string
}

export type CorrectionPromptDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  hint: string
  placeholder: string
  versionImageUrl?: string | null
  versionVideoUrl?: string | null
  closeAriaLabel: string
  cancelLabel: string
  generateLabel: string
  submittingLabel: string
  onSubmit: (prompt: string) => Promise<void>
  /** Optional chips that fill the textarea (do not submit). */
  presets?: CorrectionPromptPreset[]
  /** Raise above fullscreen hosts like ShotPanel (z 100/101). */
  nested?: boolean
}

export function CorrectionPromptDialog({
  open,
  onOpenChange,
  title,
  hint,
  placeholder,
  versionImageUrl,
  versionVideoUrl,
  closeAriaLabel,
  cancelLabel,
  generateLabel,
  submittingLabel,
  onSubmit,
  presets,
  nested = false,
}: CorrectionPromptDialogProps) {
  const [prompt, setPrompt] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [videoPreviewOpen, setVideoPreviewOpen] = useState(false)

  useEffect(() => {
    if (!open) {
      setPrompt("")
      setError(null)
    }
  }, [open])

  const handleSubmit = async () => {
    const text = prompt.trim()
    if (!text) return
    setSubmitting(true)
    setError(null)
    try {
      await onSubmit(text)
      setPrompt("")
      onOpenChange(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <WorkspaceDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={hint}
      closeAriaLabel={closeAriaLabel}
      size="lg"
      nested={nested}
      maxHeightClassName="max-h-[min(90vh,700px)]"
      bodyClassName="flex flex-col gap-4 overflow-y-auto p-5 sm:flex-row"
      footer={
        <>
          <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)} disabled={submitting}>
            {cancelLabel}
          </Button>
          <Button type="button" size="sm" disabled={submitting || !prompt.trim()} onClick={handleSubmit}>
            {submitting ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                {submittingLabel}
              </>
            ) : (
              generateLabel
            )}
          </Button>
        </>
      }
    >
      {versionVideoUrl ? (
        <div className="w-full shrink-0 sm:w-auto">
          <button
            type="button"
            className="relative h-40 w-full overflow-hidden rounded-lg border border-ui-border sm:w-40"
            onClick={() => setVideoPreviewOpen(true)}
          >
            {versionImageUrl ? (
              <img src={versionImageUrl} alt="" className="h-full w-full object-contain" />
            ) : (
              <video
                src={videoFirstFrameSrc(versionVideoUrl)}
                className="pointer-events-none h-full w-full object-contain"
                muted
                playsInline
                preload="metadata"
              />
            )}
            <span className="absolute inset-0 flex items-center justify-center">
              <PlayBadge />
            </span>
          </button>
          <ImageLightboxDialog
            open={videoPreviewOpen}
            onOpenChange={setVideoPreviewOpen}
            url={null}
            videoUrl={versionVideoUrl}
            title={title}
            nested={nested ? 2 : true}
          />
        </div>
      ) : versionImageUrl ? (
        <div className="w-full shrink-0 sm:w-auto">
          <img
            src={versionImageUrl}
            alt=""
            className="h-40 w-full rounded-lg border border-ui-border object-contain sm:w-auto"
          />
        </div>
      ) : null}
      <div className="flex w-full min-w-0 flex-1 flex-col gap-3">
        {presets && presets.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {presets.map((preset) => (
              <Button
                key={preset.id}
                type="button"
                variant="secondary"
                size="sm"
                disabled={submitting}
                onClick={() => setPrompt(preset.prompt)}
              >
                {preset.label}
              </Button>
            ))}
          </div>
        ) : null}
        <Textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={placeholder}
          rows={5}
          className="w-full flex-1 resize-none"
          disabled={submitting}
        />
        {error ? <p className="text-xs text-ui-status-failed">{error}</p> : null}
      </div>
    </WorkspaceDialog>
  )
}
